import React from 'react';
import {
  Shield,
  FileCheck,
  Sparkles
} from 'lucide-react';
import DocxCleanerWidgetClient from './DocxCleanerWidget.client';
import PagesCleanerWidgetClient from './PagesCleanerWidget.client';
import { useLocaleContext } from '../context/LocaleContext';
import type { User } from '@supabase/supabase-js';

interface DocumentCleanersProps {
  user: User | null;
}

// CLS FIX: Removed animateIn state and setTimeout - animations were no-ops causing layout shift
const DocumentCleaners: React.FC<DocumentCleanersProps> = React.memo(({ user }) => {
  const { locale } = useLocaleContext();

  return (
    <div className="mt-16">
      {/* Grid Container for Document Cleaners - Equal Heights */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-6 lg:items-stretch">
        {/* Word Document Cleaner */}
        <div className="flex">
          <DocxCleanerWidgetClient user={user} isGridLayout={true} />
        </div>

        {/* Pages Document Cleaner */}
        <div className="flex">
          <PagesCleanerWidgetClient user={user} isGridLayout={true} />
        </div>
      </div>

      {/* Shared Feature Highlights - Below Both Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-white dark:bg-charcoal-900 rounded-xl p-4 border border-neutral-200 dark:border-charcoal-700/50 shadow-sm">
          <div className="flex items-start gap-3">
            <div className="p-2 bg-purple-100 dark:bg-purple-900/30 rounded-lg">
              <Shield size={20} className="text-purple-600 dark:text-purple-400" />
            </div>
            <div>
              <h4 className="font-semibold text-neutral-900 dark:text-white text-sm">
                {locale === 'de' ? 'Vollständig privat'
                  : locale === 'es' ? 'Completamente privado'
                  : locale === 'fr' ? 'Totalement privé'
                  : locale === 'ja' ? '完全プライベート'
                  : locale === 'id' ? 'Sepenuhnya Pribadi'
                  : 'Fully Private'}
              </h4>
              <p className="text-neutral-600 dark:text-neutral-400 text-xs mt-1">
                {locale === 'de'
                  ? 'Alle Verarbeitung erfolgt in Ihrem Browser'
                  : locale === 'es'
                  ? 'Todo el procesamiento ocurre en su navegador'
                  : locale === 'fr'
                  ? 'Tout le traitement se fait dans votre navigateur'
                  : locale === 'ja'
                  ? 'すべての処理はブラウザ内で完結します'
                  : locale === 'id'
                  ? 'Semua pemrosesan dilakukan di browser Anda'
                  : 'All processing happens in your browser'}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white dark:bg-charcoal-900 rounded-xl p-4 border border-neutral-200 dark:border-charcoal-700/50 shadow-sm">
          <div className="flex items-start gap-3">
            <div className="p-2 bg-blue-100 dark:bg-blue-900/30 rounded-lg">
              <FileCheck size={20} className="text-blue-600 dark:text-blue-400" />
            </div>
            <div>
              <h4 className="font-semibold text-neutral-900 dark:text-white text-sm">
                {locale === 'de' ? 'Formatierung erhalten'
                  : locale === 'es' ? 'Conserva el formato'
                  : locale === 'fr' ? 'Préserve la mise en forme'
                  : locale === 'ja' ? 'フォーマットを維持'
                  : locale === 'id' ? 'Mempertahankan Format'
                  : 'Preserves Formatting'}
              </h4>
              <p className="text-neutral-600 dark:text-neutral-400 text-xs mt-1">
                {locale === 'de'
                  ? 'Ihr Dokument bleibt visuell identisch'
                  : locale === 'es'
                  ? 'Su documento permanece visualmente idéntico'
                  : locale === 'fr'
                  ? 'Votre document reste visuellement identique'
                  : locale === 'ja'
                  ? 'ドキュメントの見た目はそのまま維持されます'
                  : locale === 'id'
                  ? 'Dokumen Anda tetap terlihat identik'
                  : 'Your document stays visually identical'}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white dark:bg-charcoal-900 rounded-xl p-4 border border-neutral-200 dark:border-charcoal-700/50 shadow-sm">
          <div className="flex items-start gap-3">
            <div className="p-2 bg-green-100 dark:bg-green-900/30 rounded-lg">
              <Sparkles size={20} className="text-green-600 dark:text-green-400" />
            </div>
            <div>
              <h4 className="font-semibold text-neutral-900 dark:text-white text-sm">
                {locale === 'de' ? 'Sofortige Verarbeitung'
                  : locale === 'es' ? 'Procesamiento instantáneo'
                  : locale === 'fr' ? 'Traitement instantané'
                  : locale === 'ja' ? '即時処理'
                  : locale === 'id' ? 'Pemrosesan Instan'
                  : 'Instant Processing'}
              </h4>
              <p className="text-neutral-600 dark:text-neutral-400 text-xs mt-1">
                {locale === 'de'
                  ? 'Bereinigt in Sekunden'
                  : locale === 'es'
                  ? 'Limpiado en segundos'
                  : locale === 'fr'
                  ? 'Nettoyé en quelques secondes'
                  : locale === 'ja'
                  ? '数秒でクリーニング完了'
                  : locale === 'id'
                  ? 'Dibersihkan dalam hitungan detik'
                  : 'Cleaned in seconds'}
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
});

DocumentCleaners.displayName = 'DocumentCleaners';

export default DocumentCleaners;
